import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../Auth';
import { rememberReturnPath } from '../../services/returnPath';
import { apiService } from '../../services/api';

// Consentement OAuth d'un connecteur MCP (claude.ai web ou mobile, Claude Code…) : la personne connectée choisit les
// droits et la durée ; la connexion devient une clé « connecteur OAuth » visible et révocable dans Accès agents.
// Le cookie posé par /authorize accompagne les appels : seul le navigateur qui a ouvert la demande peut y répondre.
const call = (path, options = {}) => apiService.fetch(path, { credentials: 'include', ...options });

export function ConsentRoute({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) {
    rememberReturnPath(location);
    return <Navigate to="/login" replace />;
  }
  return children;
}

export default function AgentConnectorConsent() {
  const { user } = useAuth();
  const requestId = new URLSearchParams(useLocation().search).get('demande') || '';
  const [request, setRequest] = useState(null);
  const [selected, setSelected] = useState([]);
  const [days, setDays] = useState(30);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!requestId) { setError('Demande introuvable : relancez la connexion depuis Claude.'); return; }
    call(`/agent-access/oauth/requests/${encodeURIComponent(requestId)}`)
      .then((data) => { setRequest(data); setSelected(data.selected); setDays(data.default_duration); })
      .catch((err) => setError(err.message));
  }, [requestId]);

  const decide = async (approve) => {
    setBusy(true); setError('');
    try {
      const path = `/agent-access/oauth/requests/${encodeURIComponent(requestId)}/${approve ? 'approve' : 'deny'}`;
      const body = approve ? { scopes: selected, expires_in_days: Number(days) } : {};
      const { redirect_url: redirectUrl } = await call(path, { method: 'POST', body: JSON.stringify(body) });
      window.location.assign(redirectUrl);
    } catch (err) { setError(err.message); setBusy(false); }
  };

  return <main className="min-h-screen bg-gray-100 flex items-start sm:items-center justify-center p-4">
    <div className="bg-white border rounded-xl p-5 sm:p-6 w-full max-w-lg space-y-4">
      <h1 className="text-xl font-semibold">Connecter un agent à Deepflow</h1>
      {error && <p role="alert" className="bg-red-50 text-red-800 rounded p-3">{error}</p>}
      {!request && !error && <p>Chargement de la demande…</p>}
      {request && <>
        <p><strong>{request.client_name}</strong> demande à utiliser Deepflow en votre nom
          ({user?.name || user?.email}).</p>
        <p className="text-sm text-gray-600">Après votre réponse, vous serez renvoyé vers <strong>{request.redirect_host}</strong>
          {request.local ? ' (un outil de cet ordinateur)' : ''}. Si vous n’êtes pas à l’origine de cette demande, refusez-la.</p>
        <fieldset className="space-y-2"><legend className="font-medium mb-2">Droits accordés</legend>
          {request.scopes.map((scope) => <label key={scope.name} className="flex gap-2 items-start">
            <input type="checkbox" className="mt-1" checked={selected.includes(scope.name)}
              onChange={(e) => setSelected((old) => e.target.checked ? [...old, scope.name] : old.filter((s) => s !== scope.name))} />
            <span>{scope.description}</span>
          </label>)}
        </fieldset>
        <label className="flex flex-col gap-1">Durée de la connexion
          <select value={days} onChange={(e) => setDays(e.target.value)} className="border rounded p-2">
            {request.durations.map((n) => <option key={n} value={n}>{n} jours</option>)}
          </select>
        </label>
        <p className="text-sm text-gray-600">La connexion apparaît dans Accès agents &amp; API, où vous pouvez la révoquer à tout moment. Chaque appel y est journalisé ; les modifications sont attribuées à votre compte.</p>
        <div className="flex gap-3 flex-wrap">
          <button disabled={busy || !selected.length} onClick={() => decide(true)} className="bg-blue-600 text-white rounded px-4 py-2 disabled:opacity-50">Autoriser</button>
          <button disabled={busy} onClick={() => decide(false)} className="border rounded px-4 py-2">Refuser</button>
        </div>
      </>}
    </div>
  </main>;
}

export const ConsentPage = () => <ConsentRoute><AgentConnectorConsent /></ConsentRoute>;
